"use client";

import React, {useRef, useState, useEffect} from "react";

import Image from "next/image";
import CornerChip from "@/components/global/ui/CornerChip";

import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTACornerButton} from "@/shadcn/ui/corner-button";
import {HLSVideoPlayer, SanityVideoPlayer} from "@/components/ui/SanityVideoPlayer";
import {Dialog, DialogContent} from "@/shadcn/ui/dialog";

import {PageAboutSchema} from "@/sanity/pages/PageAboutTemplate";

import volumeMute from "@/public/icons/Volume--mute--filled.svg";
import volumeUnmute from "@/public/icons/Volume--up--filled.svg";
import maximize from "@/public/icons/Maximize.svg";
import screenEffect from "@/public/about/screen-effect.svg";

export default function HeroSection({
  data,
  className
}: {
  data: PageAboutSchema;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = true;
    }
  }, []);

  useEffect(() => {
    // pause background video while dialog is open
    if (videoRef.current) {
      if (isDialogOpen) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  }, [isDialogOpen]);

  const toggleMute = () => {
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
    }
  };

  const hasVideo = data?.heroVideo?.muxVideo?.playbackId || data?.heroVideo?.embedUrl;

  return (
    <section className={cn(
      "relative min-h-[122.5rem] flex",
      className
    )}>
      <Image
        className="absolute left-0 top-0 !w-full !h-full object-cover"
        src={data?.heroVideoPlaceholder}
        alt="Background image"
      />

      {/* background video */}
      {hasVideo && (
        <>
          <HLSVideoPlayer
            ref={videoRef}
            source={data.heroVideo}
            autoPlay
            muted
            loop
            playsInline
            className="absolute left-0 top-0 !w-full !h-full object-cover"
          />
          <Image
            className="absolute left-0 top-0 !w-full !h-full object-cover opacity-30 pointer-events-none"
            src={screenEffect}
            alt="screen effect"
          />
          <div className="absolute left-0 top-0 w-full h-full bg-[#0A1A1D]/60 pointer-events-none" />
        </>
      )}

      <div className="relative px-[2rem] pb-[2rem] pt-[9.5rem] sm:px-[3rem] sm:pb-[3rem] md:px-[5rem] md:pb-[5rem] lg:px-0 lg:py-[7rem] mx-auto w-[min(226rem,100%)] flex flex-col lg:flex-row-reverse lg:justify-between items-end gap-y-[43rem]">

        {/* video controls */}
        {hasVideo && (
          <div className="flex gap-[1rem]">
            {/* mute/unmute button */}
            <button
              onClick={toggleMute}
              className="group backdrop-blur-[4rem] relative px-[2.75rem] lg:px-[4rem] min-h-[5rem] lg:min-h-[7rem] flex items-center justify-center bg-white/10 [clip-path:polygon(0_0,100%_0,100%_calc(100%-2rem),calc(100%-2rem)_100%,0_100%)] hover:cursor-pointer border-0"
              aria-label={isMuted ? "Unmute video" : "Mute video"}
            >
              <div className={cn(
                "absolute left-0 top-0 h-full bg-white/40 transition-[width] group-hover:w-[8px]",
                isMuted ? "w-[4px]" : "w-[8px]"
              )} />
              <div className="relative">
                <Image
                  className="!w-[2.125rem] lg:!w-[3rem]"
                  src={isMuted ? volumeMute : volumeUnmute}
                  alt={isMuted ? "muted" : "unmuted"}
                />
              </div>
            </button>

            {/* expand video button */}
            <button
              onClick={() => setIsDialogOpen(true)}
              className="group backdrop-blur-[4rem] relative px-[2.75rem] lg:px-[4rem] min-h-[5rem] lg:min-h-[7rem] flex items-center justify-center gap-[2rem] bg-white/10 [clip-path:polygon(0_0,100%_0,100%_calc(100%-2rem),calc(100%-2rem)_100%,0_100%)] hover:cursor-pointer border-0"
              aria-label="Expand video to fullscreen"
            >
              <div className="absolute left-0 top-0 h-full w-[4px] group-hover:w-[8px] bg-white/40 transition-[width]" />
              <p className="mb-0 text-white font-medium leading-none text-[1.62rem] lg:text-[2.25rem]">Expand Video</p>
              <div className="h-[3rem] lg:h-[4rem] border-x border-l-white/50 border-r-white/10 " />
              <Image
                className="!w-[2.125rem] lg:!w-[3rem]"
                src={maximize}
                alt="maximize icon"
              />
            </button>
          </div>
        )}

        <div className="p-[3rem] sm:p-[4rem] w-[min(87.25rem,100%)] bg-white/10 backdrop-blur-[4rem] border-t-[3px] border-t-[#D9F9B9] relative flex flex-col items-start">
          <CornerChip
            className="absolute -left-[1px] bottom-full prose-img:!w-[2rem]"
            image={data?.heroChip?.icon}
            text={data?.heroChip?.text}
          />

          {data?.heroTitle && (
            <div className="richtext richtext-start prose-em:font-ivy prose-headings:text-white prose-p:text-white/60 prose-headings:w-[min(40rem,100%)] sm:prose-headings:w-[min(46rem,100%)] lg:prose-headings:w-auto">
              <PortableText
                value={data.heroTitle}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.heroCTA && (
            <CTACornerButton
              className="mt-[3rem] lg:hidden"
              data={data.heroCTA}
            />
          )}

          <hr className="border-t border-t-white/10 w-full my-[3rem]" />

          {data?.heroText && (
            <div className="richtext richtext-start prose-em:font-ivy prose-headings:text-white prose-p:text-white/60 prose-headings:w-[min(35rem,100%)] sm:prose-headings:w-[min(46rem,100%)] lg:prose-headings:w-auto">
              <PortableText
                value={data.heroText}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.heroCTA && (
            <CTACornerButton
              className="mt-[3rem] hidden lg:block"
              data={data.heroCTA}
            />
          )}
        </div>
      </div>

      {/* video overlay */}
      {hasVideo && (
        <Dialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
        >
          <DialogContent className="max-w-[min(140rem,calc(100vw-4rem))] w-full aspect-video !p-0 bg-black border-0">
            <SanityVideoPlayer
              playerData={data.heroVideo}
              playsinline={true}
              className="!w-full !h-full !rounded-[1rem]"
            />
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
